import { html, LitElement, nothing } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import _, { isEqual } from 'lodash';
import { CategoriesList, DropDownSelectEvents, ItemData } from '../../incidentsGrid/types';
import { isScrollable } from '../../../utils/htmlElement.utils';
import {
  EdrIncidentTabPanels,
  IncidentType,
  IncidentTypeIcons,
  PanelType,
  XdrIncidentTabPanels,
  XdrNodesWithoutEventsPivoting,
} from '../types';
import { translateFn } from '../../../utils/translator.helper';
import { Feature } from '@nirvana/shared/constants';
import { FontSizes } from 'apps/webcomponents/src/types/common/types';

/**
 * @summary List container that will display a list of categories for the xdr grid.
 * @tag gz-incidents-side-menu
 *
 */
export class IncidentsSideMenuWebComponent extends LitElement {
  @property({
    type: Array,
    attribute: false,
    reflect: false,
  })
  _categories: Array<CategoriesList>;

  /**
   *
   * the title of the component
   */
  @property({ type: String })
  public title = '';

  /**
   *
   */
  @property({ type: Object })
  public translations;

  /**
   *
   */
  @property({ type: String })
  public searchCategoriesPlaceholder;

  /**
   *
   *  Const used to diferentiate between a list containing entities or resources
   */
  @property({ type: Number })
  public panelType;

  /**
   *
   * type category to scroll to
   */
  @property({ type: Number })
  public categoryToScroll;

  @property({ type: String })
  private _incidentId = '';

  /**
   * @internal
   */
  @state()
  private _initialCategories;

  /**
   * @internal
   */
  @state()
  private _searchText;

  /**
   * @internal
   */
  @query('.panel-content')
  panelContent: HTMLElement;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  get categories(): Array<CategoriesList> {
    return this._categories;
  }

  /**
   * array of categories to be rendered in a list
   */
  set categories(prop: Array<CategoriesList>) {
    const oldCategories = this._categories;
    this._categories = prop;
    this._initialCategories = _.cloneDeep(this._categories);
    this.requestUpdate('categories', oldCategories);
  }

  get incidentId(): string {
    return this._incidentId;
  }

  set incidentId(id: string) {
    this._incidentId = id;
  }

  render() {
    return html`
      <div class="header mt-6 mb-1">
        <gz-title class="mb-2" fontsize="16" fontweight="500">${this.title}</gz-title>
        <gz-input-search
          @change="${this._handleInputChange}"
          placeholder="${this.searchCategoriesPlaceholder}"
          id="views-input-search"
        ></gz-input-search>
      </div>
      <div class="panel-content">
        ${this._initialCategories.map(category =>
          this._searchText?.length !== 0 && !category.items.length
            ? nothing
            : html` <gz-accordion
                isOpen="${category.isOpen ? category.isOpen : true}"
                isCompact="true"
                align="left"
                id="${category.type}"
              >
                <span slot="dynamic-title-content" class="accordion-slot d-inline-flex align-items-center">
                  <gz-icon-label icon="${category.icon}"></gz-icon-label>
                  <gz-tooltip class="accordion-title" tooltiptext="${category.text}" isellipsis="true"
                    >${category.text}</gz-tooltip
                  >
                  <gz-text class="accordion-count" fontWeight="300" fontSize="10">(${category.items.length})</gz-text>
                </span>
                ${this.renderAccordionItems(category)}
              </gz-accordion>`
        )}
      </div>
    `;
  }

  renderAccordionItems(category: CategoriesList) {
    const options = [
      {
        title: translateFn('View events and alerts'),
        value: DropDownSelectEvents.viewEvents,
      },
    ];

    return html` ${category.items.map(
      (item, index) => html`
        <div class="d-flex w-full">
          <gz-labeled-content label="${item.name}" class="flex-2 incident-side-menu-labeled-content">
            ${item.incidents ? this.renderIncidents(item.incidents, item.uid) : nothing}
          </gz-labeled-content>
          ${this.isDropDownEventsAvailableForCategory(category.type)
            ? html`
                <gz-select
                  class="side-menu-select"
                  options="${JSON.stringify(options)}"
                  @selectionChanged=${e => this.handleSelectionChanged(e.detail.selected, category.type, index, item)}
                >
                  <gz-icon-button
                    icon="ds-icon-more"
                    fontSize="24"
                    buttonType="square"
                    buttonFill="quiet"
                    buttonColor="secondary"
                  >
                  </gz-icon-button>
                </gz-select>
              `
            : nothing}
        </div>
      `
    )}`;
  }

  isDropDownEventsAvailableForCategory(categoryType) {
    if (!Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_EDR_HISTORICAL_SEARCH])) {
      return false;
    }

    if (this.panelType === PanelType.RESOURCES_PANEL) {
      return true;
    }

    return !(categoryType in XdrNodesWithoutEventsPivoting);
  }

  updated(_changedProperties) {
    this.updateComplete.then(() => this.scrollToCategory());
  }

  scrollToCategory() {
    if (isScrollable(this.panelContent) && this._searchText === undefined) {
      const category = this.shadowRoot?.getElementById(this.categoryToScroll);
      const categoryPosition = category?.offsetTop;
      const offset = 123;

      if (categoryPosition != null) {
        this.panelContent.scrollTop = categoryPosition - offset;
      }
    }
  }

  @property({
    type: Array,
    hasChanged: function (newVal: any, oldVal: any) {
      return !isEqual(newVal, oldVal);
    },
  })
  shouldUpdate(changedProperties) {
    return changedProperties.has('_initialCategories');
  }

  renderIncidents(incidents: Array<any>, uid: number) {
    return html` ${incidents.map(
      incident =>
        html`
          <div class="${this._computeCorrelatedIncidentsClass(incidents)}">
            <gz-tooltip class="tooltip" tooltipText="${this.getIncidentTypeIconTooltipText(incident.type)}">
              <gz-icon-label icon="${this._getIncidentIconByType(incident.type)}"></gz-icon-label>
            </gz-tooltip>
            <gz-link
              fontSize="${FontSizes.PARAGRAPH_12}"
              @click="${e => this._handleIncidentNumberClick(incident, uid)}"
            >
              #${this.renderIncidentNumber(incident)}</gz-link
            >
          </div>
        `
    )}`;
  }

  renderIncidentNumber(incident) {
    if (incident.type === IncidentType.XDR) {
      return incident.isGzDemo ? 'DEMO' : incident.number;
    }

    return incident.isGzDemo ? 'DEMO-' + incident.demoIncidentOrder : incident.number;
  }

  getIncidentTypeIconTooltipText(type: number) {
    return this.translations[type];
  }

  _computeCorrelatedIncidentsClass(correlatedIncidents) {
    if (correlatedIncidents.length > 1) {
      return 'group-incidents ';
    } else {
      return 'single-incident';
    }
  }

  _getIncidentIconByType(incidentType: number) {
    if (incidentType === IncidentType.XDR) {
      return IncidentTypeIcons.XDR;
    }
    if (incidentType === IncidentType.EDR) {
      return IncidentTypeIcons.EDR;
    }
  }

  _handleIncidentNumberClick(incident, uid: number) {
    let isEdrIncident = true;

    if (incident.type === IncidentType.XDR) {
      isEdrIncident = false;
    }
    if (isEdrIncident && !uid) {
      uid = 0;
    }
    let tab: EdrIncidentTabPanels | XdrIncidentTabPanels;
    if (isEdrIncident) {
      tab = EdrIncidentTabPanels.GRAPH;
    } else {
      tab = XdrIncidentTabPanels.GRAPH;
    }
    const params = {
      ...incident,
      uid: uid,
      tab: tab,
    };
    if (this.panelType === PanelType.RESOURCES_PANEL) {
      params.nodeType = 'alert';
    }
    if (this.panelType === PanelType.ENTITIES_PANEL) {
      params.nodeType = 'node';
    }
    Application.lib.edrUtils.openIncidentInNewTab(params, isEdrIncident);
  }

  _handleInputChange(event) {
    const searchInput = event.detail.sourceEvent.trim().toLowerCase();
    this._searchText = searchInput;
    this.filterCategories(searchInput);
  }

  filterCategories(filter) {
    const filteredCategories = _.cloneDeep(this.categories);
    filteredCategories.forEach(category => {
      category.items = category.items.filter(item => {
        return item.name.trim().toLowerCase().includes(filter);
      });
      if (category.items.length) {
        category.isOpen = true;
      }
    });

    this._initialCategories = filteredCategories;
  }

  handleSelectionChanged(selectedOption: DropDownSelectEvents, type: number, index: number, item: ItemData) {
    if (selectedOption === DropDownSelectEvents.viewEvents) {
      if (this.panelType === PanelType.RESOURCES_PANEL) {
        Application.lib.Navigation.openInNewTab(`/xdr/events/resources/${this.incidentId}-${type}-${index}`);
      } else {
        Application.lib.Navigation.openInNewTab(`/xdr/events/entity/${this.incidentId}-${item.uid}`);
      }
    }
  }

  constructor() {
    super();
  }

  firstUpdated(changedProperties) {
    super.firstUpdated(changedProperties);
  }
}
